import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation } from "react-router-dom";
import { getPublicPortfolio } from "@/api/db";
import { MARKET_PROFESSIONS } from "@/components/market/marketData";
import {
  MapPin, Mail, Phone, Github,
  ExternalLink, Globe, Award, Quote,
  Play, Link as LinkIcon, X, Image as ImageIcon,
  FileText, BadgeCheck, Video, Check, Copy
} from "lucide-react";

// ─── Theme ────────────────────────────────────────────────────────────────────

const ThemeCtx = React.createContext(null);

const LIGHT = {
  pageBg:  "#F2F4F4",
  card:    "#fff",
  border:  "#E5E9EA",
  text:    "#2E3233",
  sub:     "#646C6F",
  muted:   "#99A6AB",
  chip:    "#F2F4F4",
  chipTxt: "#2E3233",
  heading: "#003326",
};

const DARK = {
  pageBg:  "#0D1117",
  card:    "#161B22",
  border:  "#30363D",
  text:    "#E6EDF3",
  sub:     "#8B949E",
  muted:   "#656D76",
  chip:    "#21262D",
  chipTxt: "#C9D1D9",
  heading: "#00DCA4",
};

// ─── Category Gradients ───────────────────────────────────────────────────────

const CATEGORY_GRADIENT = {
  "Conteúdo & Criação":       "linear-gradient(135deg, #00DCA4 0%, #00664C 100%)",
  "Marketing & Performance":  "linear-gradient(135deg, #29A567 0%, #004D35 100%)",
  "Vendas & Comercial":       "linear-gradient(135deg, #F7971E 0%, #FFD200 100%)",
  "Dados & Operações":        "linear-gradient(135deg, #FEEDB4 0%, #B2911E 100%)",
  "Produto & Estratégia":     "linear-gradient(135deg, #38F9D7 0%, #00664C 100%)",
  "Comunidade & Atendimento": "linear-gradient(135deg, #F5576C 0%, #F093FB 100%)",
  "Tecnologia":               "linear-gradient(135deg, #0F2027 0%, #2C5364 100%)",
};

const DEFAULT_GRADIENT = "linear-gradient(135deg, #00DCA4 0%, #00664C 100%)";

function getGradientForProfession(professionId) {
  const prof = MARKET_PROFESSIONS.find(p => p.id === professionId);
  return prof ? (CATEGORY_GRADIENT[prof.category] || DEFAULT_GRADIENT) : DEFAULT_GRADIENT;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildWhatsAppUrl(raw) {
  const digits = raw.replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent("Olá, vi seu portfólio e quero saber mais.")}`;
}

function Chip({ children, style }) {
  const t = useContext(ThemeCtx);
  return (
    <span className="px-3 py-1 rounded-full text-xs font-medium"
      style={{ background: t.chip, color: t.chipTxt, ...style }}>
      {children}
    </span>
  );
}

function Section({ title, children }) {
  const t = useContext(ThemeCtx);
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: t.heading }}>{title}</h2>
      {children}
    </section>
  );
}

function MetricCard({ value, label }) {
  const t = useContext(ThemeCtx);
  if (!value && !label) return null;
  return (
    <div className="p-5 rounded-2xl text-center space-y-1"
      style={{ background: t.card, border: `1px solid ${t.border}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}>
      <p className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: t.heading }}>{value || "—"}</p>
      <p className="text-xs font-medium" style={{ color: t.muted }}>{label || ""}</p>
    </div>
  );
}

function CaseCard({ title, desc, result }) {
  const t = useContext(ThemeCtx);
  return (
    <div className="p-5 rounded-2xl space-y-2"
      style={{ background: t.card, border: `1px solid ${t.border}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}>
      <p className="font-bold text-sm" style={{ color: t.text }}>{title}</p>
      {desc && <p className="text-sm" style={{ color: t.sub }}>{desc}</p>}
      {result && (
        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold mt-1"
          style={{ background: t.accent + "1A", color: t.accent }}>{result}</span>
      )}
    </div>
  );
}

function CertCard({ name, issuer, url }) {
  const t = useContext(ThemeCtx);
  return (
    <div className="p-5 rounded-2xl flex flex-col items-center text-center gap-3"
      style={{ background: t.card, border: `1px solid ${t.border}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ background: "#FEEDB4" }}>
        <Award className="w-5 h-5" style={{ color: "#65541B" }} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold" style={{ color: t.text }}>{name}</p>
        {issuer && <p className="text-xs mt-1" style={{ color: t.muted }}>{issuer}</p>}
      </div>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs font-semibold"
          style={{ color: t.accent }}>
          <ExternalLink className="w-3.5 h-3.5" /> Ver certificado
        </a>
      )}
    </div>
  );
}

function TestimonialCard({ name, role, company, text }) {
  const t = useContext(ThemeCtx);
  const initial = name?.charAt(0)?.toUpperCase() || "?";
  return (
    <div className="p-5 rounded-2xl space-y-3"
      style={{ background: t.card, border: `1px solid ${t.border}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}>
      <Quote className="w-5 h-5" style={{ color: t.accent }} />
      <p className="text-sm italic" style={{ color: t.sub }}>"{text}"</p>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
          style={{ background: t.accent + "33", color: t.accent }}>{initial}</div>
        <div>
          <p className="text-sm font-bold" style={{ color: t.text }}>{name}</p>
          <p className="text-xs" style={{ color: t.muted }}>{[role, company].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
    </div>
  );
}

function PortfolioItemCard({ item, onImageClick }) {
  const t = useContext(ThemeCtx);

  // Retrocompatibility: old format { type: "image", url } → treat as single
  const imgs = item.images || (item.type === "image" && item.url ? [item.url] : []);
  const projectType = item.kind || (item.images ? item.type : "single");

  if (imgs.length === 0) {
    // Fallback: no images to show
    if (!item.title && !item.desc) return null;
    return (
      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
        <div className="w-full h-40 flex items-center justify-center" style={{ background: t.chip }}>
          <ImageIcon className="w-8 h-8" style={{ color: t.muted }} />
        </div>
        <div className="p-4" style={{ background: t.card }}>
          {item.title && <p className="font-semibold text-sm" style={{ color: t.text }}>{item.title}</p>}
          {item.desc && <p className="text-xs mt-1" style={{ color: t.muted }}>{item.desc}</p>}
        </div>
      </div>
    );
  }

  const isGallery = projectType === "gallery" && imgs.length > 1;

  return (
    <div className="rounded-2xl overflow-hidden cursor-pointer transition-shadow hover:shadow-md"
      style={{ border: `1px solid ${t.border}` }}
      onClick={() => onImageClick && onImageClick(imgs)}>
      <div className="relative">
        <img src={imgs[0]} alt={item.title || ""} className="w-full object-cover" style={{ maxHeight: "240px" }} />
        {isGallery && (
          <span className="absolute top-2 right-2 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>
            {imgs.length} fotos
          </span>
        )}
      </div>
      {(item.title || item.desc) && (
        <div className="p-4" style={{ background: t.card }}>
          {item.title && <p className="font-semibold text-sm" style={{ color: t.text }}>{item.title}</p>}
          {item.desc && <p className="text-xs mt-1" style={{ color: t.muted }}>{item.desc}</p>}
        </div>
      )}
    </div>
  );
}

function ImageLightbox({ images, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}>
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-colors"
        style={{ background: "#fff", color: "#2E3233" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F2F4F4"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
        <X className="w-6 h-6" />
      </button>
      {images.length === 1 ? (
        <div className="min-h-full flex items-center justify-center p-4">
          <img src={images[0]} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()} />
        </div>
      ) : (
        <div className="max-w-4xl mx-auto py-8 px-4 space-y-4"
          onClick={e => e.stopPropagation()}>
          {images.map((url, i) => (
            <img key={i} src={url} alt="" className="w-full object-contain rounded-lg" />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}

function GridLayout({ children, cols }) {
  const colStyle = { 2: "repeat(2, 1fr)", 3: "repeat(3, 1fr)", 4: "repeat(4, 1fr)" };
  return (
    <>
      <style>{`
        @media (min-width: 640px) {
          .grid-cols-dynamic-${cols} { grid-template-columns: ${colStyle[cols] || colStyle[2]} !important; }
        }
      `}</style>
      <div className={`grid grid-cols-1 gap-4 grid-cols-dynamic-${cols}`}>
        {children}
      </div>
    </>
  );
}

function PaginatedGrid({ children, cols, maxRows }) {
  const t = useContext(ThemeCtx);
  const items = React.Children.toArray(children);
  const perPage = maxRows > 0 ? cols * maxRows : items.length;
  const totalPages = Math.ceil(items.length / perPage);
  const [page, setPage] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (page >= totalPages && totalPages > 0) setPage(totalPages - 1);
  }, [totalPages, page]);

  if (totalPages <= 1) {
    return <GridLayout cols={cols}>{children}</GridLayout>;
  }

  const pageItems = items.slice(page * perPage, (page + 1) * perPage);

  function goTo(p) {
    setFading(true);
    setTimeout(() => { setPage(p); setFading(false); }, 150);
  }

  return (
    <div>
      <div style={{ opacity: fading ? 0 : 1, transition: "opacity 150ms ease" }}>
        <GridLayout cols={cols}>{pageItems}</GridLayout>
      </div>
      <div className="flex items-center justify-center gap-3 mt-4">
        {/* Left arrow */}
        <button
          onClick={() => goTo(page - 1)}
          disabled={page === 0}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: page === 0 ? "transparent" : (t.accent + "1A"),
            color: page === 0 ? t.muted : t.accent,
            border: `1px solid ${page === 0 ? t.border : t.accent + "44"}`,
            cursor: page === 0 ? "default" : "pointer",
            opacity: page === 0 ? 0.4 : 1,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {/* Dots */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              style={{
                width: i === page ? "20px" : "8px",
                height: "8px",
                borderRadius: "4px",
                background: i === page ? t.accent : (t.muted + "44"),
                border: "none",
                cursor: "pointer",
                transition: "all 200ms ease",
                padding: 0,
              }}
            />
          ))}
        </div>
        {/* Right arrow */}
        <button
          onClick={() => goTo(page + 1)}
          disabled={page === totalPages - 1}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: page === totalPages - 1 ? "transparent" : (t.accent + "1A"),
            color: page === totalPages - 1 ? t.muted : t.accent,
            border: `1px solid ${page === totalPages - 1 ? t.border : t.accent + "44"}`,
            cursor: page === totalPages - 1 ? "default" : "pointer",
            opacity: page === totalPages - 1 ? 0.4 : 1,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function VisualProjectsGrid({ data, cols = 2, maxRows = 0 }) {
  const [lightboxImages, setLightboxImages] = useState(null);

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <>
      <PaginatedGrid cols={cols} maxRows={maxRows}>
        {data.map((item, i) => (
          <PortfolioItemCard
            key={i}
            item={item}
            onImageClick={(imgs) => setLightboxImages(imgs)}
          />
        ))}
      </PaginatedGrid>
      {lightboxImages && (
        <ImageLightbox images={lightboxImages} onClose={() => setLightboxImages(null)} />
      )}
    </>
  );
}

// ─── Video helpers & components ───────────────────────────────────────────────

function parseVideoUrl(url) {
  if (!url) return null;
  let m;
  m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  if (m) return { provider: "youtube", id: m[1] };
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return { provider: "vimeo", id: m[1] };
  m = url.match(/loom\.com\/(?:share|embed)\/([\w]+)/);
  if (m) return { provider: "loom", id: m[1] };
  return null;
}

function getVideoThumbnail(parsed) {
  if (!parsed) return null;
  if (parsed.provider === "youtube") return `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg`;
  if (parsed.provider === "loom") return `https://cdn.loom.com/sessions/thumbnails/${parsed.id}-with-play.gif`;
  return null;
}

function getVideoEmbedUrl(parsed) {
  if (!parsed) return null;
  if (parsed.provider === "youtube") return `https://www.youtube.com/embed/${parsed.id}?autoplay=1`;
  if (parsed.provider === "vimeo") return `https://player.vimeo.com/video/${parsed.id}?autoplay=1`;
  if (parsed.provider === "loom") return `https://www.loom.com/embed/${parsed.id}?autoplay=1`;
  return null;
}

function VideoCard({ item, onPlay }) {
  const t = useContext(ThemeCtx);
  const parsed = parseVideoUrl(item.url);
  const thumb = getVideoThumbnail(parsed);
  const embedUrl = getVideoEmbedUrl(parsed);

  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer transition-shadow hover:shadow-md"
      style={{ border: `1px solid ${t.border}` }}
      onClick={() => embedUrl && onPlay(embedUrl)}>
      <div className="relative w-full" style={{ paddingBottom: "56.25%", background: "#1a1a2e" }}>
        {thumb ? (
          <img src={thumb} alt={item.title || ""} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <Video className="w-8 h-8" style={{ color: "#666" }} />
            {parsed && <span className="text-xs font-semibold" style={{ color: "#888" }}>{parsed.provider}</span>}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.25)" }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
            <Play className="w-5 h-5" style={{ color: "#fff", marginLeft: "2px" }} />
          </div>
        </div>
      </div>
      {(item.title || item.desc) && (
        <div className="p-4" style={{ background: t.card }}>
          {item.title && <p className="font-semibold text-sm" style={{ color: t.text }}>{item.title}</p>}
          {item.desc && <p className="text-xs mt-1" style={{ color: t.muted }}>{item.desc}</p>}
        </div>
      )}
    </div>
  );
}

function VideoLightbox({ embedUrl, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}>
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-colors"
        style={{ background: "#fff", color: "#2E3233" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F2F4F4"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
        <X className="w-6 h-6" />
      </button>
      <div className="w-full max-w-4xl mx-4" style={{ aspectRatio: "16/9" }}
        onClick={e => e.stopPropagation()}>
        <iframe
          src={embedUrl}
          title="Video player"
          className="w-full h-full rounded-lg"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ border: "none" }}
        />
      </div>
    </div>,
    document.body
  );
}

function VideoProjectsGrid({ data, cols = 2, maxRows = 0 }) {
  const [playUrl, setPlayUrl] = useState(null);

  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <>
      <PaginatedGrid cols={cols} maxRows={maxRows}>
        {data.map((item, i) => (
          <VideoCard key={i} item={item} onPlay={(url) => setPlayUrl(url)} />
        ))}
      </PaginatedGrid>
      {playUrl && (
        <VideoLightbox embedUrl={playUrl} onClose={() => setPlayUrl(null)} />
      )}
    </>
  );
}

function normalizeCopyData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (!("folder" in data[0])) {
    return [{ folder: "Geral", files: data.map(item => ({ title: item.title, content: item.content })) }];
  }
  return data;
}

function CopyViewer({ title, content, onClose }) {
  const t = useContext(ThemeCtx);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}>
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-colors"
        style={{ background: "#fff", color: "#2E3233" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F2F4F4"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
        <X className="w-6 h-6" />
      </button>
      <div className="max-w-3xl mx-auto my-12 p-8 rounded-2xl"
        style={{ background: t.card, color: t.text }}
        onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-6" style={{ fontFamily: "Poppins, sans-serif", color: t.heading }}>
          {title}
        </h2>
        <div
          className="copy-viewer-content"
          style={{ lineHeight: 1.7, color: t.sub }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
        <style>{`
          .copy-viewer-content h2 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.5rem; color: ${t.text}; }
          .copy-viewer-content h3 { font-size: 1.1rem; font-weight: 700; margin-bottom: 0.5rem; color: ${t.text}; }
          .copy-viewer-content p { margin-bottom: 0.75rem; }
          .copy-viewer-content ul, .copy-viewer-content ol { padding-left: 1.5rem; margin-bottom: 0.75rem; }
          .copy-viewer-content li { margin-bottom: 0.25rem; }
          .copy-viewer-content strong { font-weight: 700; }
          .copy-viewer-content em { font-style: italic; }
          .copy-viewer-content .ql-pasted-table { overflow-x: auto; margin: 0.75rem 0; }
          .copy-viewer-content .ql-pasted-table table,
          .copy-viewer-content table { width: 100%; border-collapse: collapse; font-size: 0.875rem; margin-bottom: 1rem; }
          .copy-viewer-content .ql-pasted-table td, .copy-viewer-content .ql-pasted-table th,
          .copy-viewer-content td, .copy-viewer-content th { border: 1px solid ${t.border}; padding: 8px 12px; text-align: left; }
          .copy-viewer-content th { font-weight: 600; }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

function CopywritingSection({ data, cols = 2, maxRows = 0 }) {
  const t = useContext(ThemeCtx);
  const folders = normalizeCopyData(data);
  const [activeTab, setActiveTab] = useState(0);
  const [viewerFile, setViewerFile] = useState(null);

  if (folders.length === 0) return null;

  const currentFiles = folders[activeTab]?.files || [];

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        {folders.map((f, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors"
            style={{
              background: i === activeTab ? (t.accent + "1A") : t.chip,
              color: i === activeTab ? t.accent : t.chipTxt,
              border: i === activeTab ? `1px solid ${t.accent}` : `1px solid transparent`,
            }}>
            {f.folder || "Sem nome"}
          </button>
        ))}
      </div>

      <PaginatedGrid key={activeTab} cols={cols} maxRows={maxRows}>
        {currentFiles.map((file, i) => (
          <div
            key={i}
            onClick={() => setViewerFile(file)}
            className="p-5 rounded-2xl flex items-center gap-4 cursor-pointer transition-shadow hover:shadow-md"
            style={{ background: t.card, border: `1px solid ${t.border}`, boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: t.chip }}>
              <FileText className="w-5 h-5" style={{ color: t.muted }} />
            </div>
            <p className="font-semibold text-sm truncate" style={{ color: t.text }}>
              {file.title || "Sem título"}
            </p>
          </div>
        ))}
      </PaginatedGrid>

      {viewerFile && (
        <CopyViewer
          title={viewerFile.title}
          content={viewerFile.content}
          onClose={() => setViewerFile(null)}
        />
      )}
    </>
  );
}

// ─── Section Labels ───────────────────────────────────────────────────────────

const SECTION_LABELS = {
  projects:        "Projetos",
  visual_projects: "Projetos Visuais",
  text_samples:    "Copywriting",
  metrics:         "Resultados",
  cases:           "Cases de Sucesso",
  tech_projects:   "Projetos Tech",
  tools:           "Ferramentas",
  certifications:  "Certificações",
  testimonials:    "Depoimentos",
};

const SECTION_ORDER = [
  "projects", "metrics", "cases",
  "tech_projects", "tools", "certifications", "testimonials",
];

// ─── Dynamic Section Renderer ─────────────────────────────────────────────────

function DynamicSection({ id, data }) {
  const t = useContext(ThemeCtx);
  const label = SECTION_LABELS[id] || id;
  const cols = t.columns || 2;
  const maxRows = t.maxRows || 0;

  switch (id) {
    case "projects": {
      if (!Array.isArray(data) || data.length === 0) return null;
      const visualItems = data.filter(item => item.kind === "image" || item.kind === "gallery" || !item.kind);
      const videoItems = data.filter(item => item.kind === "video");
      const copyItems = data.filter(item => item.kind === "copywriting");
      const hasMultipleTypes = [visualItems.length > 0, videoItems.length > 0, copyItems.length > 0].filter(Boolean).length > 1;
      if (visualItems.length === 0 && videoItems.length === 0 && copyItems.length === 0) return null;
      return (
        <Section title={label}>
          {visualItems.length > 0 && <VisualProjectsGrid data={visualItems} cols={cols} maxRows={maxRows} />}
          {videoItems.length > 0 && (
            <div style={visualItems.length > 0 ? { marginTop: "3rem" } : undefined}>
              {hasMultipleTypes && <h3 className="text-base font-bold mb-3" style={{ fontFamily: "Poppins, sans-serif", color: t.heading }}>Vídeos</h3>}
              <VideoProjectsGrid data={videoItems} cols={cols} maxRows={maxRows} />
            </div>
          )}
          {copyItems.length > 0 && (
            <div style={(visualItems.length > 0 || videoItems.length > 0) ? { marginTop: "3rem" } : undefined}>
              {hasMultipleTypes && <h3 className="text-base font-bold mb-3" style={{ fontFamily: "Poppins, sans-serif", color: t.heading }}>Copywriting</h3>}
              <CopywritingSection data={copyItems.map(item => ({ folder: item.folder, files: item.files }))} cols={cols} maxRows={maxRows} />
            </div>
          )}
        </Section>
      );
    }

    // Legacy support for old portfolios
    case "visual_projects": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <VisualProjectsGrid data={data} cols={cols} maxRows={maxRows} />
        </Section>
      );
    }

    case "text_samples": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <CopywritingSection data={data} cols={cols} maxRows={maxRows} />
        </Section>
      );
    }

    case "metrics": {
      if (!Array.isArray(data)) return null;
      const filled = data.filter(m => m.value || m.label);
      if (filled.length === 0) return null;
      return (
        <Section title={label}>
          <PaginatedGrid cols={cols} maxRows={maxRows}>
            {filled.map((m, i) => <MetricCard key={i} value={m.value} label={m.label} />)}
          </PaginatedGrid>
        </Section>
      );
    }

    case "cases": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <PaginatedGrid cols={cols} maxRows={maxRows}>
            {data.map((c, i) => <CaseCard key={i} {...c} />)}
          </PaginatedGrid>
        </Section>
      );
    }

    case "tech_projects": {
      if (!data || typeof data !== "object") return null;
      const { github_url = "", projects = [] } = data;
      if (!github_url && projects.length === 0) return null;
      const githubUser = github_url.split("github.com/")[1]?.replace(/\/$/, "");
      return (
        <>
          {github_url && (
            <Section title="GitHub">
              <a href={github_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-2xl transition-colors"
                style={{ background: t.card, border: `1px solid ${t.border}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "#0F2027" }}>
                  <Github className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: t.text }}>{githubUser || "Ver GitHub"}</p>
                  <p className="text-xs" style={{ color: t.muted }}>github.com</p>
                </div>
                <ExternalLink className="w-4 h-4" style={{ color: t.muted }} />
              </a>
            </Section>
          )}
          {projects.length > 0 && (
            <Section title="Projetos">
              <PaginatedGrid cols={cols} maxRows={maxRows}>
                {projects.map((p, i) => (
                  <div key={i} className="p-5 rounded-2xl space-y-3"
                    style={{ background: t.card, border: `1px solid ${t.border}` }}>
                    <p className="font-bold text-sm" style={{ color: t.text }}>{p.name}</p>
                    {p.desc && <p className="text-xs" style={{ color: t.sub }}>{p.desc}</p>}
                    {(p.stack || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {p.stack.map(s => (
                          <span key={s} className="px-2 py-0.5 rounded text-xs font-mono"
                            style={{ background: t.chip, color: t.chipTxt }}>{s}</span>
                        ))}
                      </div>
                    )}
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs font-semibold"
                        style={{ color: "#00664C" }}>
                        <ExternalLink className="w-3.5 h-3.5" /> Ver projeto
                      </a>
                    )}
                  </div>
                ))}
              </PaginatedGrid>
            </Section>
          )}
        </>
      );
    }

    case "tools": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <div className="flex flex-wrap gap-2">
            {data.map(tool => <Chip key={tool}>{tool}</Chip>)}
          </div>
        </Section>
      );
    }

    case "certifications": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <PaginatedGrid cols={cols} maxRows={maxRows}>
            {data.map((c, i) => <CertCard key={i} {...c} />)}
          </PaginatedGrid>
        </Section>
      );
    }

    case "testimonials": {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <Section title={label}>
          <PaginatedGrid cols={cols} maxRows={maxRows}>
            {data.map((t, i) => <TestimonialCard key={i} {...t} />)}
          </PaginatedGrid>
        </Section>
      );
    }

    default:
      return null;
  }
}

// ─── PortfolioView ────────────────────────────────────────────────────────────

export function PortfolioView({ portfolio: p, hideCta = false }) {
  const [emailCopied, setEmailCopied] = useState(false);
  const pd = p.portfolio_data || {};
  const colorMode = pd.color_mode || p.color_mode || "white";
  const baseTheme = colorMode === "dark" ? DARK : LIGHT;
  const accent = pd.accent_color || p.accent_color || "#00DCA4";
  const columns = pd.columns || 2;
  const maxRows = pd.max_rows || 0;
  const theme = {
    ...baseTheme,
    accent,
    heading: colorMode === "dark" ? accent : baseTheme.heading,
    columns,
    maxRows,
  };
  const gs = pd.gradient_start || p.gradient_start;
  const ge = pd.gradient_end || p.gradient_end;
  const gradient = gs && ge
    ? `linear-gradient(135deg, ${gs} 0%, ${ge} 100%)`
    : getGradientForProfession(p.profession);
  const portfolioData = p.portfolio_data || {};
  const maxWidthMap = { 2: "672px", 3: "896px", 4: "1152px" };
  const wrapperMaxWidth = maxWidthMap[columns] || "672px";

  return (
    <ThemeCtx.Provider value={theme}>
      <div className="min-h-screen" style={{ background: theme.pageBg }}>

        {/* Hero cover */}
        <div className="relative h-52 w-full"
          style={p.cover_url
            ? { background: `url(${p.cover_url}) center/cover no-repeat` }
            : { background: gradient }}>
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.15)" }} />
        </div>

        {/* Content wrapper */}
        <div className="mx-auto px-4 -mt-14 pb-16 space-y-12 relative z-10" style={{ maxWidth: wrapperMaxWidth }}>

          {/* Avatar + identity */}
          <div className="space-y-4">
            <div className="w-28 h-28 rounded-full overflow-hidden flex-shrink-0"
              style={{
                background: theme.chip,
                border: "4px solid " + theme.card,
                boxShadow: "0 2px 12px 0 rgba(0,0,0,0.12)",
              }}>
              {p.avatar_url
                ? <img src={p.avatar_url} alt={p.full_name} className="w-full h-full object-cover" />
                : (
                  <div className="w-full h-full flex items-center justify-center text-3xl font-bold"
                    style={{ background: gradient, color: "#fff" }}>
                    {p.full_name?.charAt(0) || "?"}
                  </div>
                )}
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Poppins, sans-serif", color: theme.heading }}>
                {p.full_name}
                {p.subscription_started_at && (new Date() - new Date(p.subscription_started_at)) >= 30 * 24 * 60 * 60 * 1000 && (
                  <BadgeCheck className="w-6 h-6 flex-shrink-0" style={{ color: '#00DCA4' }} title="Selo Talent Pro" />
                )}
              </h1>
              {p.headline && (
                <p className="text-base font-medium" style={{ color: theme.sub }}>{p.headline}</p>
              )}
              {p.location && (
                <div className="flex items-center gap-1.5 text-sm" style={{ color: theme.muted }}>
                  <MapPin className="w-3.5 h-3.5" /> {p.location}
                </div>
              )}
            </div>

            {/* Contact buttons */}
            <div className="flex flex-wrap gap-2">
              {p.whatsapp && (
                <a href={buildWhatsAppUrl(p.whatsapp)} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold"
                  style={{ background: "#25D366", color: "#fff" }}>
                  <Phone className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {p.email_contact && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(p.email_contact);
                    setEmailCopied(true);
                    setTimeout(() => setEmailCopied(false), 2000);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all"
                  style={{ background: theme.card, color: theme.text, border: `1px solid ${emailCopied ? "#00DCA4" : theme.border}` }}>
                  {emailCopied
                    ? <><Check className="w-4 h-4" style={{ color: "#00DCA4" }} /> Copiado!</>
                    : <><Copy className="w-4 h-4" /> E-mail</>
                  }
                </button>
              )}
            </div>
          </div>

          {/* Bio */}
          {p.bio && (
            <section className="p-5 rounded-2xl text-sm"
              style={{ background: theme.card, border: `1px solid ${theme.border}`, lineHeight: 1.7 }}>
              <h3 className="text-sm font-bold mb-2" style={{ color: theme.accent }}>Sobre mim</h3>
              <p className="whitespace-pre-wrap" style={{ color: theme.sub }}>{p.bio}</p>
            </section>
          )}

          {/* Dynamic sections — supports new blocks array or legacy flat format */}
          {Array.isArray(portfolioData.blocks)
            ? portfolioData.blocks.map(block => (
                <DynamicSection key={block.id} id={block.type} data={block.data} />
              ))
            : SECTION_ORDER.map(sectionId => {
                const sectionData = portfolioData[sectionId];
                if (sectionData == null) return null;
                return <DynamicSection key={sectionId} id={sectionId} data={sectionData} />;
              })
          }


        </div>
      </div>
    </ThemeCtx.Provider>
  );
}

export default function PublicPortfolio() {
  const { slug: pathSlug } = useParams();
  const location = useLocation();
  const querySlug = new URLSearchParams(location.search).get("slug");
  const slug = pathSlug || querySlug;
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    load();
  }, [slug]);

  async function load() {
    setLoading(true);
    try {
      const data = await getPublicPortfolio(slug);
      if (!data) { setNotFound(true); }
      else { setPortfolio(data); }
    } catch {
      setNotFound(true);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F2F4F4" }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center space-y-6"
        style={{ background: "#F2F4F4" }}>
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto"
          style={{ background: "#EAFAF6" }}>
          <Globe className="w-10 h-10" style={{ color: "#00664C" }} />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Portfólio não encontrado
          </h1>
          <p className="text-sm" style={{ color: "#646C6F" }}>
            Este portfólio não existe ou ainda não foi publicado.
          </p>
        </div>
        <a href="/?page=MyPortfolio"
          className="px-6 py-3 rounded-full font-bold text-sm"
          style={{ background: "#00DCA4", color: "#003326" }}>
          Criar meu portfólio
        </a>
      </div>
    );
  }

  return <PortfolioView portfolio={portfolio} />;
}
